// Pure aggregations over the district issue feed (GET /issues → GisIssue[]).
// Shared by the Issues tab (B1 list + B4 breakdown) and the map overlay (B1b).
// "Active" = not completed. "Critical" = critical severity AND active.

import { type IssueStatus } from '@/shared/constants/issue-status'

import type { GisIssue, GisSite } from '../api/types'

export function isActive(i: GisIssue): boolean {
  return i.status !== 'completed'
}

/** Whole days since an issue was raised (clamped at 0). The "age" used by targets, sorting, and KPIs. */
export function ageDays(createdAt: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000))
}

export function isCritical(i: GisIssue): boolean {
  return i.severity === 'critical' && isActive(i)
}

/** Active issues only (completed excluded). */
export function activeIssues(list: GisIssue[]): GisIssue[] {
  return list.filter(isActive)
}

/**
 * The merged status view shown on the Issues board: `open` folds the stored `open` and `pending`
 * states into one bucket (= every non-`completed` status), so the board shows only
 * **All / Open / Closed** instead of a 3-way split. View KEYS keep their v1 names ('closed' =
 * completed issues) — they're persisted UI state, not API values.
 */
export type IssueStatusView = 'all' | 'open' | 'closed'

/** Does an issue status belong to the given merged view? `open` = not completed; `closed` = completed; `all` = any. */
export function matchesView(status: IssueStatus, view: IssueStatusView): boolean {
  if (view === 'all') {
    return true
  }
  if (view === 'closed') {
    return status === 'completed'
  }
  return status !== 'completed' // open = open + pending (every non-completed status)
}

/** Whole-feed counts for the All / Open / Closed summary pills (severity-agnostic). */
export function viewCounts(list: GisIssue[]): { all: number; open: number; closed: number } {
  let open = 0
  let closed = 0
  for (const i of list) {
    if (i.status === 'completed') {
      closed += 1
    } else {
      open += 1
    }
  }
  return { all: list.length, open, closed }
}

export interface SiteIssueCount {
  open: number
  critical: number
}

/**
 * Per-site counts over a PRE-FILTERED list — no `isActive` guard, so the caller decides what's in
 * `list` (this is what lets the "resolved"/closed group render). Issues without a site are skipped;
 * `critical` is the critical-severity subset of the same list.
 */
export function siteIssueCounts(list: GisIssue[]): Map<string, SiteIssueCount> {
  const out = new Map<string, SiteIssueCount>()
  for (const i of list) {
    if (i.siteId === null) {
      continue
    }
    const c = out.get(i.siteId) ?? { open: 0, critical: 0 }
    c.open += 1
    if (i.severity === 'critical') {
      c.critical += 1
    }
    out.set(i.siteId, c)
  }
  return out
}

/**
 * Active-issue counts keyed by `siteId` (closed + site-less excluded). Powers the default map overlay:
 * `open` = all active at the site, `critical` = the critical subset.
 */
export function issuesBySite(list: GisIssue[]): Map<string, SiteIssueCount> {
  return siteIssueCounts(activeIssues(list))
}

/** Markers to draw: all sites, or only those present in `counts` when focusing issues (Issues tab). */
export function filterSitesWithIssues(
  sites: GisSite[],
  counts: Map<string, SiteIssueCount>,
  issuesFocus: boolean,
): GisSite[] {
  if (!issuesFocus) {
    return sites
  }
  return sites.filter((s) => counts.has(s.id))
}

export interface IssueBreakdown {
  total: number
  bySeverity: { critical: number; non_critical: number }
  byCategory: { label: string; count: number }[]
}

const UNCATEGORISED = 'Uncategorised'

/** Severity split + category tally over ACTIVE issues (B4). Categories sorted by count desc. */
export function issueBreakdown(list: GisIssue[]): IssueBreakdown {
  const active = activeIssues(list)
  const bySeverity = { critical: 0, non_critical: 0 }
  const cats = new Map<string, number>()
  for (const i of active) {
    bySeverity[i.severity] += 1
    const label = i.category ?? UNCATEGORISED
    cats.set(label, (cats.get(label) ?? 0) + 1)
  }
  const byCategory = [...cats.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
  return { total: active.length, bySeverity, byCategory }
}
